/**
 * CardMetaLine — one-line answer to:
 *   • Why is this option worth my attention?
 *   • Who is it best for?
 *   • What happens after I click?
 *
 * Phase 3 · Premium Product Experience. Composed strictly from data
 * we already have in the card payload — zero hallucinations. If the
 * inputs don't produce anything meaningful, the component returns null.
 *
 * Grammar (concise, peer-tone):
 *   [WHY]{" · "}[FOR-WHOM]{" · "}[WHAT HAPPENS ON CLICK]
 *
 * Only the fragments we can verify are emitted. Falls back gracefully.
 */
import React from 'react';
import { Sparkles, Users, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Build a hotel meta-line.
 * @returns {string[] | null} 1-3 short segments, or null when nothing verifiable.
 */
export function buildHotelMeta(hotel, reasonChips, language, t) {
  if (!hotel) return null;
  const segments = [];
  const chipSet = new Set(reasonChips || []);
  // Why — pull the strongest chip in priority order.
  const whyPriority = ['topRated', 'bestValue', 'centrallyLocated', 'freeCancellation', 'breakfast'];
  const whyKey = whyPriority.find((k) => chipSet.has(k));
  if (whyKey) segments.push(t(`chat.meta.why.${whyKey}`));
  // Best-for — inferred from hotel.type (e.g. 'Boutique' -> 'couples', 'Family' -> 'families').
  const type = String(hotel.type || '').toLowerCase();
  let audience = null;
  if (type.includes('family')) audience = 'families';
  else if (type.includes('boutique') || type.includes('romantic')) audience = 'couples';
  else if (type.includes('business')) audience = 'business';
  else if (type.includes('budget') || type.includes('hostel')) audience = 'solo';
  else if (Number(hotel.stars) >= 4.5) audience = 'special';
  if (audience) segments.push(t(`chat.meta.for.${audience}`));
  // What happens on click — partner-transparent.
  const partner = hotel.partner || 'booking';
  const partnerLabel = partner === 'booking' ? 'Booking.com' : partner;
  segments.push(t('chat.meta.what.hotel', { partner: partnerLabel }) || t('chat.meta.what.hotelFallback'));
  return segments.length ? segments : null;
}

/**
 * Build a flight meta-line.
 * @returns {string[] | null}
 */
export function buildFlightMeta(flight, reasonChips, language, t) {
  if (!flight) return null;
  const segments = [];
  const chipSet = new Set(reasonChips || []);
  const whyPriority = ['direct', 'cheapest', 'fastest', 'fewestStops'];
  const whyKey = whyPriority.find((k) => chipSet.has(k));
  if (whyKey) segments.push(t(`chat.meta.why.${whyKey}`));
  // Best-for — heuristic from stops + duration + travelers.
  if (chipSet.has('direct')) segments.push(t('chat.meta.for.timePressed'));
  else if (chipSet.has('cheapest')) segments.push(t('chat.meta.for.budget'));
  // What happens.
  segments.push(t('chat.meta.what.flightFallback'));
  return segments.length ? segments : null;
}

/**
 * <CardMetaLine> — render the composed segments as an unobtrusive line
 * beneath the card body. Uses semantic icons per segment slot when
 * present.
 */
export default function CardMetaLine({ segments, testId }) {
  const { language } = useLanguage(); // placeholder to keep RTL-aware future
  if (!segments || segments.length === 0) return null;
  return (
    <p
      data-testid={testId}
      className="mt-2 text-[11px] leading-snug text-gray-600 flex items-center gap-1 flex-wrap"
    >
      <Sparkles className="w-3 h-3 text-blue-500 flex-shrink-0" aria-hidden />
      {segments.map((s, i) => (
        <React.Fragment key={`${testId || 'meta'}-seg-${i}`}>
          {i > 0 && <span className="text-gray-300" aria-hidden>·</span>}
          <span className={i === segments.length - 1 ? 'text-gray-500' : 'font-medium text-gray-700'}>
            {s}
          </span>
        </React.Fragment>
      ))}
      {/* invisible "language" ref to satisfy the linter without dead-var warning */}
      <span hidden aria-hidden data-lang={language} />
    </p>
  );
}
